import React from 'react';
import { serviceArtSize, serviceIconCrop } from 'fym-shared/src/music-services/serviceIconCrops';
import { musicServiceImagesByImporterId } from '../../../common/musicServiceImages';
import { TypedMemo } from '../../../common/types';
import { integrationTypeToImporterId } from '../../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
import { useSMLTheme } from '../../../music24/theme/useSMLTheme';

/** The size of a mark on a card (in the fixed box) and in the modal head. */
const CARD = { weight: 26, max: { width: 64, height: 32 } };
const LARGE = { weight: 36, max: { width: 100, height: 44 } };

/** The full lockup: 50 units high, about twice as wide. */
const LOCKUP_HEIGHT = 50;

interface CatalogServiceIconProps {
  integrationType: string;
  isLarge?: boolean;
}

/**
 * The brand icon of a music service, for a Catalog card.
 *
 * The logo files are lockups (icon and name) with empty space above and below. The
 * card names the service in text, so the mark draws the icon alone: the file's
 * viewBox becomes the icon's rectangle (`serviceIconCrops`). Every icon gets the same
 * visual weight. The ink follows the theme: the `light` file (dark ink) on the light
 * theme, the `dark` file (light ink) on the dark theme, so Apple Music and Tidal show
 * on both. A service with no crop draws its whole lockup, contained in the box.
 */
const CatalogServiceIconComponent: React.FC<CatalogServiceIconProps> = ({ integrationType, isLarge }) => {
  const { theme } = useSMLTheme();
  const importerId = integrationTypeToImporterId[integrationType];
  const images = importerId === undefined ? undefined : musicServiceImagesByImporterId[importerId];
  if (images === undefined) {
    return null;
  }
  const Logo = theme === 'dark' ? images.logoColor.dark : images.logoColor.light;
  const { weight, max } = isLarge === true ? LARGE : CARD;
  const crop = serviceIconCrop(integrationType);
  if (crop === undefined) {
    return (
      <Logo
        height={Math.min(max.height, LOCKUP_HEIGHT)}
        width={max.width}
        style={{ flexShrink: 0 }}
        data-testid={`service-icon-${integrationType}`}
      />
    );
  }
  const rect = theme === 'dark' ? crop.dark : crop.light;
  const size = serviceArtSize(rect, crop.isIcon, weight, max);
  return (
    <Logo
      viewBox={`${rect.x} ${rect.y} ${rect.width} ${rect.height}`}
      width={size.width}
      height={size.height}
      style={{ flexShrink: 0, overflow: 'hidden' }}
      data-testid={`service-icon-${integrationType}`}
    />
  );
};

export const CatalogServiceIcon = TypedMemo(CatalogServiceIconComponent);
